import React, { useEffect, useRef, useState } from 'react';
import { Line } from 'react-chartjs-2';
import type {
  ActiveElement,
  Chart as ChartJS,
  ChartEvent,
  ChartOptions,
  InteractionModeMap,
  TooltipModel,
} from 'chart.js';
import 'chart.js/auto';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '@/redux/store';
import { useUser, useAuth } from '@clerk/nextjs';
import { fetchStressLevelData } from '@/redux/reducers/stressLevelSlice';
import { useTranslate } from '@tolgee/react';
import { formatDate } from '@/utils/helpers';

interface StressLevelChartProps {
  maxWidth?: string;
}

const StressLevelChart: React.FC<StressLevelChartProps> = ({ maxWidth = '700px' }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useUser();
  const { getToken } = useAuth();
  const chartRef = useRef<ChartJS<'line'> | null>(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const { t } = useTranslate();
  const { labels, data } = useSelector((state: RootState) => state.stressLevel);

  useEffect(() => {
    const fetchData = async () => {
      if (labels.length === 0 && data.length === 0 && user?.id) {
        const token = await getToken();
        if (token) {
          void dispatch(fetchStressLevelData({ userId: user.id, token }));
        }
      }
    };
    void fetchData();
  }, [dispatch, user?.id, labels.length, data.length, getToken]);

  const getStressLevelContent = (score: number) => {
    if (score < 7) {
      return {
        title: t('stresstest.survey-result.content.title.0-7-points.label'),
        description: t('stresstest.survey-result.stresslevel_explanation.0-7-points.label'),
      };
    } else if (score < 14) {
      return {
        title: t('stresstest.survey-result.content.title.8-14-points.label'),
        description: t('stresstest.survey-result.stresslevel_explanation.8-14-points.label'),
      };
    } else {
      return {
        title: t('stresstest.survey-result.content.title.15-21-points.label'),
        description: t('stresstest.survey-result.stresslevel_explanation.15-21-points.label'),
      };
    }
  };

  const externalTooltipHandler = (context: { tooltip: TooltipModel<'line'> }) => {
    const tooltipEl = document.getElementById('chartjs-tooltip');
    if (!tooltipEl || (labels[0] === '0' && labels[1] === '0')) return;

    if (context.tooltip.opacity === 0) {
      tooltipEl.style.opacity = '0';
      return;
    }

    const score = parseInt(context.tooltip.dataPoints[0].formattedValue);
    const label = formatDate(context.tooltip.dataPoints[0].label);
    const tooltipContent = getStressLevelContent(score);
    const scoreLabel = t('stresstest.result.points_count.label');

    tooltipEl.innerHTML = `
      <div class="p-5 bg-white rounded-lg" style="box-shadow: 0px 0px 87px 0px rgba(90, 94, 98, 0.07), 0px 0px 14.137px 0px rgba(90, 94, 98, 0.04);">
        <div class="text-base mb-1">${tooltipContent.description}</div>  
        <div class="text-base mb-1">${scoreLabel} ${score}</div>
        <div class="text-base">${label}</div>
      </div>
    `;

    const tooltipWidth = tooltipEl.offsetWidth;
    tooltipEl.style.opacity = '1';
    tooltipEl.style.position = 'absolute';
    tooltipEl.style.left = `${tooltipPos.x - tooltipWidth / 2}px`;
    tooltipEl.style.top = `${tooltipPos.y + 10}px`;
    tooltipEl.style.pointerEvents = 'none';
  };

  const chartData = {
    labels: labels,
    datasets: [
      {
        label: 'Stress Level',
        data: data,
        borderColor: '#2B3BF4',
        fill: true,
        tension: 0.4,
        pointBackgroundColor: '#2B3BF4',
        pointHoverBackgroundColor: '#2B3BF4',
        pointBorderColor: '#1c2def',
        pointHoverBorderColor: 'rgba(43, 59, 244, 0.5)',
        pointHoverBorderWidth: 5,
        pointRadius: 0,
        pointHoverRadius: 5,
      },
    ],
  };

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: 0 },
    animation: { duration: 200 },
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false, external: externalTooltipHandler },
    },
    scales: {
      x: { grid: { display: false }, ticks: { display: false, padding: 0 } },
      y: { grid: { display: false }, ticks: { display: false } },
    },
    interaction: {
      mode: 'nearest' as keyof InteractionModeMap,
      intersect: false,
    },
    events: ['mousemove', 'mouseout'],
    onHover: (event: ChartEvent, chartElement: ActiveElement[]) => {
      if (chartElement.length) {
        const nativeEvent = event.native as MouseEvent;
        setTooltipPos({ x: nativeEvent.offsetX, y: nativeEvent.offsetY });
      }
    },
  };

  useEffect(() => {
    if (chartRef.current) {
      const gradient = chartRef.current.ctx.createLinearGradient(0, 0, 0, chartRef.current.height);
      gradient.addColorStop(0, '#1C2DEF');
      gradient.addColorStop(1, '#000');
      chartRef.current.data.datasets[0].backgroundColor = gradient;
      chartRef.current.update();
    }

    const handleMouseLeave = () => {
      const tooltipEl = document.getElementById('chartjs-tooltip');
      if (tooltipEl) tooltipEl.style.opacity = '0';
    };

    chartRef.current?.canvas.addEventListener('mouseleave', handleMouseLeave);
    chartRef.current?.canvas.addEventListener('mouseout', handleMouseLeave);

    return () => {
      chartRef.current?.canvas.removeEventListener('mouseleave', handleMouseLeave);
      chartRef.current?.canvas.removeEventListener('mouseout', handleMouseLeave);
    };
  }, [labels, data]);

  return (
    <div
      className={`mb-5 stress-chart -ml-2 w-[calc(100%+10px)] max-w-[${maxWidth}] relative rounded-lg`}
    >
      <Line ref={chartRef} data={chartData} options={options} />
      <div id="chartjs-tooltip" style={{ opacity: '0' }}></div>
    </div>
  );
};

export default StressLevelChart;
